import { Button } from '@bpinternal/ui-kit-next'
import { useSuspenseQuery as useTanstackSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { BannerPortal, Boundary, Page } from '~/components'
import { BpSpinner, Spinner } from '~/elements'
import { useAnnouncements } from '~/features/announcements/hooks/useAnnouncement'
import { SaleEventBannerWrapper } from '~/features/billing/components/SaleEventBanner'
import { CreateBotButton } from '~/features/bots/components'
import { AuditsPreview, BotsView } from '~/features/home/components'
import { WorkspaceSocialHeader } from '~/features/workspaces/components'
import { useCreateBotFlow } from '~/hooks/bots'
import { useFeature } from '~/hooks/useFeature'
import { useMigrateWorkspaceToV4 } from '~/hooks/workspaces'
import { SidebarLayout } from '~/layouts'
import { userPreferencesQueryOptions } from '~/queries'
import { getQueryOptions, useSuspenseQuery } from '~/services'
import { BotsUsage } from '../../../features/home/components/BotsUsage'
import { MembersPreview } from '../../../features/home/components/MembersPreview'

export const Route = createFileRoute('/workspaces/$workspaceId/home')({
  component: WorkspaceComponent,
})

function WorkspaceComponent() {
  const { workspaceId } = Route.useParams()
  const workspace = useTanstackSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId })).data
  const { user } = Route.useRouteContext()
  const { data: bots = [] } = useSuspenseQuery('workspaces_/$workspaceId_/bots_', { workspaceId })
  const migrateWorkspaceToV4Flag = useFeature('migrate-workspace-to-v4')
  const canMigrateToV4 = migrateWorkspaceToV4Flag && workspace.billingVersion === 'v3'
  const migrateToV4 = useMigrateWorkspaceToV4(workspaceId)

  const { data: recentBots } = useTanstackSuspenseQuery(
    userPreferencesQueryOptions({ path: '$workspaceId/botHistory', params: { workspaceId } })
  )

  // Viber experiment (PD-427): auto-open the bot-type selector when landing on a workspace
  // that has no bots yet. Dismissable — closing it falls back to the empty-state Home.
  const isViberEnabled = useFeature('dashboard-viber')
  const { startCreateBotFlow } = useCreateBotFlow({ workspaceId })
  const hasAutoPromptedRef = useRef(false)

  useEffect(() => {
    if (isViberEnabled && bots.length === 0 && !hasAutoPromptedRef.current) {
      hasAutoPromptedRef.current = true
      void startCreateBotFlow()
    }
  }, [isViberEnabled, bots.length, startCreateBotFlow])

  useAnnouncements({
    workspaceId,
    botId: recentBots[0],
    targetAnnouncementIds: [
      'conversation-insights',
      'billing-refresh',
      'webchat-config-refresh',
      'webchat-v3.4',
      'auto-recharge-addons',
      'webchat-v3.5',
    ],
  })

  return (
    <Boundary showErrorIcon errorIconSize={5} loader={<BpSpinner />}>
      <Page
        title={
          <div className="flex items-start md:items-center">
            <WorkspaceSocialHeader workspace={workspace} />
          </div>
        }
        actions={
          <div className="hidden w-fit items-center sm:flex">
            <MembersPreview workspaceId={workspaceId} />
          </div>
        }
        primaryActions={<CreateBotButton workspaceId={workspaceId} className="h-full w-full gap-2 md:w-fit" />}
      >
        {canMigrateToV4 && (
          <div className="mb-2 flex items-center justify-between gap-4 rounded-lg bg-muted/50 px-4 py-2">
            <p className="text-sm text-muted-foreground">
              This workspace is on billing v3. Migrate to the new billing system to access the latest features.
            </p>
            <Button variant="outline" size="sm" onClick={() => migrateToV4.mutate()} disabled={migrateToV4.isPending}>
              {migrateToV4.isPending ? <Spinner size="2" /> : 'Migrate to v4'}
            </Button>
          </div>
        )}
        <BannerPortal>
          <Boundary onError={() => null} suspenseFallback={null}>
            <SaleEventBannerWrapper workspaceId={workspaceId} />
          </Boundary>
        </BannerPortal>
        <SidebarLayout
          main={
            <div className="flex flex-col gap-4">
              <Boundary showErrorIcon errorIconSize={5}>
                <BotsView bots={bots} workspaceId={workspaceId} userId={user.id} />
              </Boundary>

              <div className="mt-2 flex flex-col gap-4 lg:hidden">
                <Boundary className="h-48" loaderSize="7" onError={() => null}>
                  <BotsUsage workspaceId={workspaceId} />
                </Boundary>
                <Boundary className="h-80" loaderSize="7">
                  <AuditsPreview workspaceId={workspaceId} />
                </Boundary>
              </div>
            </div>
          }
          rightSidebar={
            <div className="hidden flex-col gap-6 lg:flex">
              <Boundary className="h-48" loaderSize="7" onError={() => null}>
                <BotsUsage workspaceId={workspaceId} />
              </Boundary>
              <Boundary className="h-80" loaderSize="7">
                <AuditsPreview workspaceId={workspaceId} />
              </Boundary>
            </div>
          }
        />
      </Page>
    </Boundary>
  )
}
